import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { AsyncAppThunk } from "../application-state";

export interface UiStore {
  isLoading: boolean;
  locale: string;
  snacks: {
    [key: string]: { message: string; color?: string };
  };
  apiProgress: {
    isVisible: boolean;
    progress: number;
    message?: string;
  };
}

export const initialState: UiStore = {
  isLoading: false,
  locale: localStorage.getItem("language") || "ar",
  snacks: {},
  apiProgress: {
    isVisible: false,
    progress: 0,
    message: undefined,
  },
};

const slice = createSlice({
  name: "ui",
  initialState,
  reducers: {
    showLoader: (state: UiStore) => {
      state.isLoading = true;
    },
    hideLoader: (state: UiStore) => {
      state.isLoading = false;
    },
    addSnack: (
      state: UiStore,
      action: PayloadAction<{ message: string; color?: string }>,
    ) => {
      state.snacks = {
        ...state.snacks,
        [Object.keys(state.snacks).length]: action.payload,
      };
    },
    removeSnack: (state: UiStore) => {
      delete state.snacks[Object.keys(state.snacks)[0]];
    },
    setLocale: (state: UiStore, action: PayloadAction<string>) => {
      state.locale = action.payload;
      localStorage.setItem("language", action.payload);
      window.location.reload();
    },
    updateApiProgress: (
      state: UiStore,
      action: PayloadAction<{ progress: number; message?: string }>,
    ) => {
      state.apiProgress.progress = action.payload.progress;
      if (action.payload.message !== undefined) {
        state.apiProgress.message = action.payload.message;
      }
    },
    showApiProgress: (
      state: UiStore,
      action: PayloadAction<{ message?: string }>,
    ) => {
      state.apiProgress.isVisible = true;
      state.apiProgress.progress = 0;
      state.apiProgress.message = action.payload.message;
    },
    hideApiProgress: (state: UiStore) => {
      state.apiProgress.isVisible = false;
    },
    resetApiProgress: (state: UiStore) => {
      state.apiProgress = initialState.apiProgress;
    },
  },
});

export const {
  showLoader,
  hideLoader,
  addSnack,
  removeSnack,
  setLocale,
  updateApiProgress,
  showApiProgress,
  hideApiProgress,
  resetApiProgress,
} = slice.actions;

export const reducer = slice.reducer;

export const showLoaderWhile =
  (action: () => Promise<any>): AsyncAppThunk =>
  async (dispatch, store) => {
    dispatch(showLoader());

    try {
      await action();
    } finally {
      dispatch(hideLoader());
    }
  };
